import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import {
  LayoutGrid, Search, TrendingUp, Link2,
  Target, Globe, FileText, Edit, Trash2, ChevronLeft,
  Download, Mail, MapPin, BarChart3, Sparkles, Gauge, MessageSquareQuote, Snowflake,
} from "lucide-react";
import ClientFormDialog from "../components/ClientFormDialog";
import { toast } from "sonner";
import { API_BASE } from "../lib/api";

import OverviewTab from "../components/client/OverviewTab";
import AIInsightsTab from "../components/client/AIInsightsTab";
import AIVisibilityTab from "../components/client/AIVisibilityTab";
import BrandMentionsTab from "../components/client/BrandMentionsTab";
import KeywordsTab from "../components/client/KeywordsTab";
import RankTab from "../components/client/RankTab";
import BacklinksTab from "../components/client/BacklinksTab";
import CompetitorsTab from "../components/client/CompetitorsTab";
import SerpTab from "../components/client/SerpTab";
import NotesTab from "../components/client/NotesTab";
import LocalSEOTab from "../components/client/LocalSEOTab";
import GscPanel from "../components/client/GscPanel";
import PageInsightsTab from "../components/client/PageInsightsTab";
import TabFreshnessNote from "../components/client/TabFreshnessNote";

// Lightweight wrapper so the Search Console tab follows the same `Component({client})` contract
function SearchConsoleTab({ client, reloadClient }) {
  return (
    <div className="space-y-5" data-testid="search-console-tab">
      <TabFreshnessNote testid="gsc-freshness">
        Cached for 24h on the server · Google Search Console itself reports with a 2-3 day lag, so today&apos;s numbers always end 3 days ago.
      </TabFreshnessNote>
      <GscPanel client={client} reloadClient={reloadClient} />
    </div>
  );
}

const ALL_SECTIONS = [
  { key: "overview", label: "Overview", icon: LayoutGrid, Component: OverviewTab, always: true },
  { key: "ai-insights", label: "AI Insights", icon: Sparkles, Component: AIInsightsTab, always: true },
  { key: "ai-visibility", label: "GEO", icon: Sparkles, Component: AIVisibilityTab, always: true },
  { key: "brand-mentions", label: "Brand Mentions", icon: MessageSquareQuote, Component: BrandMentionsTab, always: true },
  { key: "keywords", label: "Keywords", icon: Search, Component: KeywordsTab, always: true },
  { key: "ranks", label: "Rank tracking", icon: TrendingUp, Component: RankTab, always: true },
  { key: "local", label: "Local SEO", icon: MapPin, Component: LocalSEOTab, localOnly: true },
  { key: "backlinks", label: "Backlinks", icon: Link2, Component: BacklinksTab, always: true },
  { key: "competitors", label: "Competitors", icon: Target, Component: CompetitorsTab, always: true },
  { key: "serp", label: "SERP", icon: Globe, Component: SerpTab, always: true },
  { key: "search-console", label: "Search Console", icon: BarChart3, Component: SearchConsoleTab, always: true },
  { key: "page-insights", label: "Page Insights", icon: Gauge, Component: PageInsightsTab, always: true },
  { key: "notes", label: "Notes & Reports", icon: FileText, Component: NotesTab, always: true },
];

export default function ClientDetail() {
  const { id, section } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const active = section || "overview";
  // Filter out local-only tabs for national campaigns
  const sections = useMemo(
    () => ALL_SECTIONS.filter((s) => s.always || (s.localOnly && client?.campaign_type === "local")),
    [client?.campaign_type],
  );
  const ActiveComponent = sections.find((s) => s.key === active)?.Component || OverviewTab;

  const load = async () => {
    try {
      const { data } = await api.get(`/clients/${id}`);
      setClient(data);
    } catch (err) {
      toast.error("Client not found");
      navigate("/clients");
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/clients/${id}`);
        if (alive) setClient(data);
      } catch (err) {
        toast.error("Client not found");
        navigate("/clients");
      }
    })();
    return () => { alive = false; };
  }, [id, navigate]);

  const onDelete = async () => {
    if (!window.confirm("Delete this client?")) return;
    await api.delete(`/clients/${id}`);
    toast.success("Client removed.");
    navigate("/clients");
  };

  const onDownloadPdf = () => {
    const token = localStorage.getItem("seo_token");
    const url = `${API_BASE}/clients/${id}/report.pdf?token=${encodeURIComponent(token)}`;
    window.open(url, "_blank");
  };

  const onSendDigest = async () => {
    if (!client?.report_email) {
      toast.error("Set a 'Monthly digest email' on the client first (Edit).");
      return;
    }
    if (!window.confirm(`Send monthly digest now to ${client.report_email}?`)) return;
    setSendingDigest(true);
    try {
      const { data } = await api.post(`/clients/${id}/send-digest`);
      toast.success(`Digest sent to ${data.sent_to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Digest send failed");
    } finally {
      setSendingDigest(false);
    }
  };

  if (!client) {
    return (
      <div className="text-sm text-[#a0aec0] font-mono-tight pt-6">Loading client…</div>
    );
  }

  return (
    <div className="space-y-5 pt-2" data-testid="client-detail-root">
      <div className="flex items-center gap-2 text-xs text-[#718096]">
        <button
          onClick={() => navigate("/clients")}
          className="flex items-center gap-1 hover:text-[#2c7a7b] transition-colors"
          data-testid="back-to-clients"
        >
          <ChevronLeft className="h-3 w-3" /> Clients
        </button>
        <span className="text-[#cbd5e0]">/</span>
        <span className="text-[#1a202c] font-medium">{client.name}</span>
      </div>

      <div className="card-soft p-6 flex items-end justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          {client.logo_url ? (
            <img
              src={client.logo_url}
              alt={client.name}
              className="h-14 w-14 rounded-2xl object-contain bg-white border border-[#edf2f7] p-1"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <div className="h-14 w-14 rounded-2xl bg-brand text-white flex items-center justify-center text-base font-bold">
              {client.name.slice(0, 2).toUpperCase()}
            </div>
          )}
          <div>
            <h1 className="font-display text-2xl text-[#1a202c]" data-testid="client-name">
              {client.name}
            </h1>
            <div className="flex items-center gap-3 mt-1 text-xs text-[#718096]">
              <a
                href={`https://${client.domain}`}
                target="_blank"
                rel="noreferrer"
                className="font-mono-tight hover:text-[#2c7a7b] transition-colors"
                data-testid="client-domain-link"
              >
                {client.domain} ↗
              </a>
              <span className="text-[#cbd5e0]">·</span>
              <span className="font-mono-tight">{client.location_name}</span>
              {client.campaign_type === "local" && client.service_location && (
                <>
                  <span className="text-[#cbd5e0]">·</span>
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#4fd1c5]/15 text-[#2c7a7b] border border-[#4fd1c5]/40 text-[10px] font-bold uppercase tracking-widest"
                    data-testid="client-local-badge"
                    title={`Local SERP target: ${client.service_location}`}
                  >
                    Local · {client.service_location.split(",")[0]}
                  </span>
                </>
              )}
              <span className="text-[#cbd5e0]">·</span>
              <span className="font-mono-tight">{client.keywords?.length || 0} kw</span>
              {client.frozen && (
                <>
                  <span className="text-[#cbd5e0]">·</span>
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#ebf8ff] text-[#2b6cb0] border border-[#90cdf4] text-[10px] font-bold uppercase tracking-widest"
                    data-testid="client-frozen-badge"
                    title={client.frozen_at ? `Frozen since ${new Date(client.frozen_at).toLocaleDateString()}` : "Frozen"}
                  >
                    <Snowflake className="h-3 w-3" /> Frozen
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={onDownloadPdf} data-testid="download-pdf" className="btn-ghost inline-flex items-center gap-2">
            <Download className="h-3.5 w-3.5" /> PDF
          </button>
          <button onClick={onSendDigest} disabled={sendingDigest || client.frozen} title={client.frozen ? "Client is frozen — no emails are sent" : undefined} data-testid="send-digest" className="btn-ghost inline-flex items-center gap-2">
            <Mail className="h-3.5 w-3.5" /> {sendingDigest ? "Sending…" : "Send digest"}
          </button>
          <button onClick={() => setEditOpen(true)} data-testid="edit-client" className="btn-ghost inline-flex items-center gap-2">
            <Edit className="h-3.5 w-3.5" /> Edit
          </button>
          <button
            onClick={onDelete}
            data-testid="delete-client-detail"
            className="btn-ghost inline-flex items-center gap-2 hover:!bg-[#fed7d7]/30 hover:!border-[#f56565]/30 hover:!text-[#c53030]"
          >
            <Trash2 className="h-3.5 w-3.5" /> Delete
          </button>
        </div>
      </div>

      {client.frozen && (
        <div className="card-soft px-5 py-3 flex items-center gap-3 text-xs text-[#2b6cb0] bg-[#ebf8ff] border border-[#90cdf4]" data-testid="client-frozen-banner">
          <Snowflake className="h-4 w-4 shrink-0" />
          <span>
            <strong>This client is frozen.</strong> You're viewing saved data only — no rank checks, API lookups or emails run until you unfreeze it in <strong>Edit</strong>.
          </span>
        </div>
      )}

      {/* Section tabs */}
      <div className="card-soft p-1.5 sticky top-16 z-20">
        <div className="flex items-center gap-1 overflow-x-auto">
          {sections.map((s) => {
            const Icon = s.icon;
            const isActive = active === s.key;
            return (
              <button
                key={s.key}
                onClick={() => navigate(`/clients/${id}/${s.key}`)}
                data-testid={`tab-${s.key}`}
                className={`flex items-center gap-2 px-3.5 py-2 text-xs whitespace-nowrap rounded-xl transition-all font-semibold ${
                  isActive
                    ? "bg-brand text-white shadow-[0_4px_10px_-2px_rgba(56,178,172,0.4)]"
                    : "text-[#4a5568] hover:bg-[#f7fafc]"
                }`}
              >
                <Icon className="h-3.5 w-3.5" strokeWidth={2} />
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      <ActiveComponent client={client} reloadClient={load} />

      <ClientFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        initial={client}
        onSaved={(c) => {
          setClient(c);
          setEditOpen(false);
        }}
      />
    </div>
  );
}
